import React from 'react';

import { useMutation } from '@apollo/client';
import { Button } from '@newsela/angelou';

import { mutations } from '@client/common/graph';
import { QUESTION_TYPES } from '@client/utils/create-assessments';
import * as toast from '@client/utils/toast';

import { $footer, $cancelButton, $nextButton } from './style';
import { TABS } from '../StandardsSelector';

const Footer = ({ activeTab, selectedStandards, appliedStandards, setActiveTab, handleClose, content }) => {
  const isReviewTab = activeTab === TABS.REVIEW;

  // Only selected standards should be used by the set mutation
  const standardIdsToSet = selectedStandards.map((standard) => standard.id);

  const originallyAppliedIds = content?.metadataStandards?.map((s) => s.id) || [];
  const currentlyAppliedIds = appliedStandards.map((s) => s.id);
  // Only soft-deleted standards should be used by the unset mutation.
  const standardIdsToUnset = originallyAppliedIds.filter((id) => !currentlyAppliedIds.includes(id));

  const [setContent, { loading: setLoading }] = useMutation(mutations.setContent);
  const [unsetContent, { loading: unsetLoading }] = useMutation(mutations.unsetContent);
  const [setAssessmentQuestion, { loading: setAssessmentQuestionLoading }] = useMutation(mutations.setAssessmentQuestion);
  const [unsetAssessmentQuestion, { loading: unsetAssessmentQuestionLoading }] = useMutation(mutations.unsetAssessmentQuestion);

  const isApplyDisabled =
    (standardIdsToSet.length === 0 && standardIdsToUnset.length === 0) || setLoading ||
    unsetLoading || setAssessmentQuestionLoading || unsetAssessmentQuestionLoading;

  const applyChanges = async () => {
    const loadingToast = toast.loading('Applying standards changes...');

    try {
      const promises = [];
      const setMethod = QUESTION_TYPES.includes(content?.questionType) ? setAssessmentQuestion : setContent;
      const unsetMethod = QUESTION_TYPES.includes(content?.questionType) ? unsetAssessmentQuestion : unsetContent;

      if (standardIdsToSet.length > 0) {
        promises.push(
          setMethod({
            variables: {
              input: {
                uid: content.uid,
                metadataStandardsIds: standardIdsToSet,
              },
            },
          })
        );
      }

      if (standardIdsToUnset.length > 0) {
        promises.push(
          unsetMethod({
            variables: {
              input: {
                uid: content.uid,
                metadataStandardsIds: standardIdsToUnset,
              },
            },
          })
        );
      }

      await Promise.all(promises);

      handleClose({ applyAndResetCache: true });
    } catch (error) {
      toast.error('Error while saving the changes. Please try again.');
    } finally {
      loadingToast.hide();
    }
  };

  const handleApplyChanges = () => {
    if (!isReviewTab) {
      setActiveTab(TABS.REVIEW);
      return;
    }

    applyChanges();
  };

  return (
    <footer css={$footer}>
      <Button flavor='SECONDARY' onClick={() => handleClose()} __cssFor={{ root: $cancelButton }}>
        Cancel
      </Button>
      <Button
        __cssFor={{ root: $nextButton }}
        onClick={handleApplyChanges}
        disabled={isApplyDisabled}
      >
        {isReviewTab ? 'Save changes' : 'Next: Review Standards'}
      </Button>
    </footer>
  );
};

export default Footer;
